import React, { useCallback, useMemo, useState } from 'react';
import { Skeleton } from 'primereact/skeleton';
import { useSetAtom } from 'jotai';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { selectWidgetAIAtom } from '~/atoms/dashboardAtom';
import { useWidgetPopout } from '~/hooks/useWidgetPopout';
import { NoDataAvailableTemplate } from '../../common/NoDataAvailableTemplate';
import { WidgetHeader } from '../../common/WidgetHeader';
import { IndicatorDot } from '../../misc/widget/indicatorDot';
import { MetricLabel } from '../../common/MetricLabel';

interface PortfolioMetric {
  label: string;
  value: string;
  isPositive?: boolean;
  currentValue?: string;
  proposedValue?: string;
  changeValue?: string;
  description?: string | React.ReactNode;
  // For metrics with ex-ante and ex-post splits
  exAnteCurrentValue?: string;
  exAnteProposedValue?: string;
  exPostCurrentValue?: string;
  exPostProposedValue?: string;
  stressTests?: { name: string; value: string; isPositive?: boolean }[];
}

export type ProposedPortfolioWidgetProps = {
  data: PortfolioMetric[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  snapshotId?: string | null;
};

export const ProposedPortfolioWidget: React.FC<
  ProposedPortfolioWidgetProps
> = ({
  data,
  isLoading,
  error,
  refetch,
  handleRemoveWidget,
  widgetId,
  title = 'Current VS AI Portfolio',
  snapshotId,
}) => {
  const [parseError, setParseError] = useState<Error | null>(null);
  const setSelectWidgetAI = useSetAtom(selectWidgetAIAtom);

  const removeWidget = useCallback(
    () => handleRemoveWidget?.(widgetId),
    [widgetId, handleRemoveWidget],
  );

  // Add popout functionality
  const handlePopout = useWidgetPopout({
    widgetId,
    widgetComponent: ProposedPortfolioWidget,
    title: title || 'Current VS AI Portfolio',
    defaultWidth: 1000,
    defaultHeight: 800,
    widgetProps: {
      data,
      isLoading,
      error,
      refetch,
      handleRemoveWidget,
      widgetId,
      title,
      snapshotId,
    },
  });

  const chartData = useMemo(() => {
    try {
      return data;
    } catch (error) {
      setParseError(error as Error);
      return data;
    }
  }, [data]);

  const noData = useMemo(() => {
    return !chartData.length;
  }, [chartData]);

  // Separate metrics with current and proposed values for table view
  const { tableMetrics, cardMetrics } = useMemo(() => {
    const table: PortfolioMetric[] = [];
    const cards: PortfolioMetric[] = [];

    chartData.forEach((metric) => {
      // Include in table if it has currentValue and proposedValue
      if (metric.currentValue && metric.proposedValue) {
        table.push(metric);
      } else if (metric.exPostCurrentValue && metric.exPostProposedValue) {
        // Only show Ex-Post metrics (skip Ex-Ante)
        // Calculate change value for Ex-Post
        const currentVal = parseFloat(
          metric.exPostCurrentValue.replace('%', ''),
        );
        const proposedVal = parseFloat(
          metric.exPostProposedValue.replace('%', ''),
        );
        const change = proposedVal - currentVal;
        const hasPercent = metric.exPostCurrentValue.includes('%');

        table.push({
          ...metric,
          label: metric.label, // Keep original label without "(Ex-Post)" suffix
          currentValue: metric.exPostCurrentValue,
          proposedValue: metric.exPostProposedValue,
          changeValue: !isNaN(change)
            ? `${change >= 0 ? '+' : ''}${change.toFixed(hasPercent ? 1 : 2)}${hasPercent ? '%' : ''}`
            : undefined,
          isPositive: !isNaN(change) ? change <= 0 : undefined, // Lower is better for risk metrics
          // Clear ex-ante/ex-post fields so it's treated as a regular row
          exAnteCurrentValue: undefined,
          exAnteProposedValue: undefined,
          exPostCurrentValue: undefined,
          exPostProposedValue: undefined,
        });
      } else {
        cards.push(metric);
      }
    });

    return { tableMetrics: table, cardMetrics: cards };
  }, [chartData]);

  if (isLoading) {
    return (
      <div className="p-3 h-full">
        <Skeleton className="h-full" />
      </div>
    );
  }

  if (error || parseError) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="Something went wrong."
          isError
        />
      </div>
    );
  }

  if (!snapshotId) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="No Snapshot Available."
          subTitle="Run portfolio analysis to generate snapshot"
        />
      </div>
    );
  }

  if (noData) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          title="No data found."
          icon="pi pi-chart-line"
        />
      </div>
    );
  }

  return (
    <div className="hover-container h-full flex flex-column" id={widgetId}>
      <WidgetHeader
        title={title}
        removeWidget={removeWidget}
        refetch={() => refetch()}
        setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
        onPopout={handlePopout}
        widgetId={widgetId}
      />

      {/* Full Height Content with Scrollable Table and Cards */}
      <div className="flex-1 p-3 overflow-y-auto">
        {/* Table View for Metrics with Current and Proposed Values */}
        {tableMetrics.length > 0 && (
          <div
            className="mb-3"
            style={{
              borderRadius: '8px',
              overflow: 'hidden',
              border: '1px solid var(--bq-border-muted)',
            }}
          >
            <DataTable
              value={tableMetrics}
              stripedRows
              size="small"
              className="proposed-portfolio-table"
              style={{ fontFamily: 'var(--font-family)' }}
            >
              <Column
                field="label"
                header="Metric"
                style={{ width: '40%' }}
                body={(rowData: PortfolioMetric) => (
                  <div
                    className="flex flex-column"
                    style={{ padding: '0.5rem 0' }}
                  >
                    <span
                      className="font-semibold"
                      style={{
                        color: 'var(--bq-text-primary)',
                        fontSize: '0.875rem',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      {rowData.label}
                    </span>
                  </div>
                )}
              />
              <Column
                field="currentValue"
                header="Current"
                alignHeader="center"
                style={{ width: '20%', textAlign: 'center' }}
                body={(rowData: PortfolioMetric) => (
                  <div
                    className="flex justify-content-center"
                    style={{ padding: '0.5rem 0' }}
                  >
                    <span
                      style={{
                        color: 'var(--bq-text-primary)',
                        fontSize: '0.875rem',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      {rowData.currentValue}
                    </span>
                  </div>
                )}
              />
              <Column
                field="proposedValue"
                header="Proposed"
                alignHeader="center"
                style={{ width: '20%', textAlign: 'center' }}
                body={(rowData: PortfolioMetric) => (
                  <div
                    className="flex justify-content-center"
                    style={{ padding: '0.5rem 0' }}
                  >
                    <span
                      className="font-semibold"
                      style={{
                        color: 'var(--bq-text-primary)',
                        fontSize: '0.875rem',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      {rowData.proposedValue}
                    </span>
                  </div>
                )}
              />
              <Column
                field="changeValue"
                header="Change"
                alignHeader="center"
                style={{ width: '20%', textAlign: 'center' }}
                body={(rowData: PortfolioMetric) => (
                  <div
                    className="flex align-items-center justify-content-center gap-2"
                    style={{ padding: '0.5rem 0' }}
                  >
                    {rowData.changeValue ? (
                      <>
                        <span
                          className="font-semibold"
                          style={{
                            color:
                              rowData.isPositive === true
                                ? 'var(--bq-color-positive)'
                                : rowData.isPositive === false
                                  ? 'var(--bq-color-negative)'
                                  : 'var(--bq-text-primary)',
                            fontSize: '0.875rem',
                            fontFamily: 'var(--font-family)',
                          }}
                        >
                          {rowData.changeValue}
                        </span>
                        {rowData.isPositive !== undefined && (
                          <IndicatorDot
                            color={rowData.isPositive ? 'green' : 'red'}
                          />
                        )}
                      </>
                    ) : (
                      <span
                        style={{
                          color: 'var(--bq-text-tertiary)',
                          fontSize: '0.875rem',
                          fontFamily: 'var(--font-family)',
                        }}
                      >
                        -
                      </span>
                    )}
                  </div>
                )}
              />
            </DataTable>
          </div>
        )}

        {/* Card View for Other Metrics */}
        {cardMetrics.length > 0 && (
          <div className="grid h-full">
            {cardMetrics.map((metric, index) => (
              <div
                key={`${metric.label}-${index}`}
                className={
                  metric.label === 'Stress Test' ? 'col-12' : 'col-12 md:col-6'
                }
              >
                <Card
                  className="h-full flex flex-column"
                  pt={{
                    body: { className: 'p-3 flex-1' },
                    content: { className: 'p-0 m-0 flex-1' },
                  }}
                >
                  <MetricLabel
                    label={metric.label}
                    description={metric.description}
                  />

                  {metric.stressTests ? (
                    // Show stress test items in clean format
                    <div className="flex flex-column gap-2">
                      {metric.stressTests.map((test, index) => (
                        <div
                          key={`${test.name}-${index}`}
                          className="flex justify-content-between align-items-center"
                        >
                          <span
                            className="text-sm"
                            style={{ color: 'var(--bq-text-secondary)' }}
                          >
                            {test.name}
                          </span>
                          <div className="flex align-items-center gap-2">
                            <span
                              className="font-semibold"
                              style={{
                                color:
                                  test.isPositive === true
                                    ? 'var(--bq-color-positive)'
                                    : test.isPositive === false
                                      ? 'var(--bq-color-negative)'
                                      : 'var(--bq-text-primary)',
                              }}
                            >
                              {test.value}
                            </span>
                            {test.isPositive !== undefined && (
                              <IndicatorDot
                                color={test.isPositive ? 'green' : 'red'}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Show single value (proposed/default behavior)
                    <div className="flex flex-column gap-1">
                      <span
                        className="text-lg font-semibold"
                        style={{
                          color:
                            metric.isPositive === true
                              ? 'var(--bq-color-positive)'
                              : metric.isPositive === false
                                ? 'var(--bq-color-negative)'
                                : 'var(--bq-text-primary)',
                        }}
                      >
                        {metric.value}
                      </span>
                      {metric.isPositive !== undefined && (
                        <div className="flex justify-content-end">
                          <IndicatorDot
                            color={metric.isPositive ? 'green' : 'red'}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
